import { ChangeEvent, useCallback } from "react";
import { IconButton, styled } from "@mui/material";
import { normalizeFileName } from "../utils/utils";

import DynamicIcon from "./dynamic_icon";
import PrimaryButton from "./primary_button";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * DefaultDownloadInput
 */

type Props = {
  label?: string;
  saving: boolean;
  filename: string | null;
  maxFilenameSize?: number;
  accept?: string;
  value?: File | null;
  onChange?: (file: File | null) => void;
  onDownloadFile?: (filename: string) => void;
  onDeleteFile?: (filename: string) => void;
};

export const DefaultDownloadInput = ({
  label,
  saving,
  filename,
  maxFilenameSize = 40,
  accept,
  value,
  onChange,
  onDownloadFile,
  onDeleteFile,
}: Props) => {
  const handleDownloadFile = useCallback(() => {
    if (!saving) {
      if (filename && filename.length > 0) {
        if (filename && filename.length > 0) {
          onDownloadFile?.(filename);
        }
      }
    }
  }, [
    saving,
    filename,
    onDownloadFile,
  ]);

  const handleDeleteFile = useCallback(() => {
    if (!saving) {
      if (filename && filename.length > 0) {
        onDeleteFile?.(filename);
      }
    }
  }, [
    saving,
    filename,
    onDeleteFile,
  ]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (!saving && e.target.files) {
      const f = e.target.files.item(0);
      if (f) {
        onChange?.(f);
      }
    }
  }, [
    saving,
    onChange,
  ]);

  return (
    (filename && filename.length > 0) ? (
      <div className="w-full flex flex-row justify-between items-center">
        <span
          className="cursor-pointer"
          onMouseDown={handleDownloadFile}>
          {filename && filename && filename.length > maxFilenameSize
            ? `${filename.substring(0, maxFilenameSize)}...`
            : filename}
        </span>
        <IconButton onClick={handleDeleteFile}>
          <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
        </IconButton>
      </div>
    ) : (
      <PrimaryButton
        component="label"
        role={undefined}
        variant="contained"
        tabIndex={-1}
        disabled={saving}
        startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
        {value ? normalizeFileName((value as any).name, maxFilenameSize) : (label ?? "Escolher arquivo")}
        <VisuallyHiddenInput
          type="file"
          accept={accept}
          onChange={(e) => handleFileChange(e)}
          multiple={false} />
      </PrimaryButton>
    )
  );
}